import React, { useMemo, useEffect } from 'react';
import { Command } from 'cmdk';
import PropTypes from 'prop-types';
import {
  User,
  Building2,
  List,
  ArrowUp,
  ArrowDown,
  CornerDownLeft,
  Search,
  Command as CommandIcon,
} from 'lucide-react';
import {
  UniversalSearchGlobalStyles,
  SearchContainer,
  DialogTitle,
  SearchInputWrapper,
  SearchIconWrapper,
  TabsContainer,
  Tab,
  TabCount,
  SearchList,
  EmptyState,
  LoadingState,
  LoadingText,
  SearchGroup,
  SearchItem,
  ItemIcon,
  ItemAvatar,
  ItemContent,
  ItemLabel,
  ItemDescription,
  Footer,
  FooterAction,
  FooterKey,
} from './styles';
import { useSearchResults } from './hooks/useSearchResults';
import { trackEvent } from '../../../helpers/analytics';

export const UniversalSearch = ({ open, onOpenChange }) => {
  const {
    search,
    setSearch,
    contacts,
    lists,
    loading,
    handleSelect,
    activeTab,
    handleTabChange,
    totalCount,
    contactsCount,
    listsCount,
    hasSearched,
  } = useSearchResults(onOpenChange);

  const isMac = useMemo(() => typeof navigator !== 'undefined' && /Mac/i.test(navigator.userAgent), []);
  const showFooter = hasSearched && !loading && (contacts.length > 0 || lists.length > 0);

  useEffect(() => {
    if (open) {
      trackEvent('universal_search:open');
    }
  }, [open]);

  // Reset search and tabs when dialog is closed
  useEffect(() => {
    if (!open) {
      setSearch('');
      handleTabChange('all');
    }
  }, [open, setSearch, handleTabChange]);

  return (
    <>
      <UniversalSearchGlobalStyles />
      <Command.Dialog open={open} onOpenChange={onOpenChange} label="Universal Search" shouldFilter={false}>
        <DialogTitle>Universal Search</DialogTitle>
        <SearchContainer>
          <SearchInputWrapper>
            <SearchIconWrapper>
              <Search size={16} aria-hidden="true" />
            </SearchIconWrapper>
            <Command.Input value={search} onValueChange={setSearch} placeholder="Search contacts and lists" />
          </SearchInputWrapper>
          {hasSearched && !loading && (
            <TabsContainer>
              <Tab $active={activeTab === 'all'} onClick={() => handleTabChange('all')}>
                All
                <TabCount $active={activeTab === 'all'}>{totalCount}</TabCount>
              </Tab>
              <Tab $active={activeTab === 'contacts'} onClick={() => handleTabChange('contacts')}>
                Contacts
                <TabCount $active={activeTab === 'contacts'}>{contactsCount}</TabCount>
              </Tab>
              <Tab $active={activeTab === 'lists'} onClick={() => handleTabChange('lists')}>
                Lists
                <TabCount $active={activeTab === 'lists'}>{listsCount}</TabCount>
              </Tab>
            </TabsContainer>
          )}
          <SearchList as={Command.List}>
            {loading && (
              <LoadingState as={Command.Loading}>
                <LoadingText>Searching...</LoadingText>
              </LoadingState>
            )}

            {hasSearched && !loading && contacts.length === 0 && lists.length === 0 && (
              <EmptyState as={Command.Empty}>No results found</EmptyState>
            )}

            {!loading && contacts.length > 0 && (
              <SearchGroup as={Command.Group} heading={activeTab === 'all' ? 'Contacts' : undefined}>
                {contacts.map((contact) => (
                  <SearchItem
                    as={Command.Item}
                    key={`contact-${contact.id}`}
                    value={`contact-${contact.id}-${contact.label}`}
                    onSelect={() => handleSelect(contact.url, 'contact')}
                  >
                    <ItemIcon $isContact>
                      {contact.icon ? (
                        <ItemAvatar $isContact src={contact.icon} alt="" />
                      ) : (
                        <User size={16} aria-hidden="true" />
                      )}
                    </ItemIcon>
                    <ItemContent>
                      <ItemLabel>{contact.label}</ItemLabel>
                      {contact.position && <ItemDescription>{contact.position}</ItemDescription>}
                    </ItemContent>
                  </SearchItem>
                ))}
              </SearchGroup>
            )}

            {!loading && lists.length > 0 && (
              <SearchGroup as={Command.Group} heading={activeTab === 'all' ? 'Lists' : undefined}>
                {lists.map((list) => (
                  <SearchItem
                    as={Command.Item}
                    key={`list-${list.id}`}
                    value={`list-${list.id}-${list.label}`}
                    onSelect={() => handleSelect(list.url, 'list')}
                  >
                    <ItemIcon $isContact={false}>
                      {list.kind === 'companies' ? (
                        <Building2 size={16} aria-hidden="true" />
                      ) : (
                        <List size={16} aria-hidden="true" />
                      )}
                    </ItemIcon>
                    <ItemContent>
                      <ItemLabel>{list.label}</ItemLabel>
                      {list.path && <ItemDescription>{list.path}</ItemDescription>}
                    </ItemContent>
                  </SearchItem>
                ))}
              </SearchGroup>
            )}
          </SearchList>
          {showFooter && (
            <Footer>
              <div style={{ display: 'flex', gap: '16px' }}>
                <FooterAction>
                  <FooterKey>
                    <ArrowUp size={12} aria-hidden="true" />
                  </FooterKey>
                  <FooterKey>
                    <ArrowDown size={12} aria-hidden="true" />
                  </FooterKey>
                  <span>Navigate</span>
                </FooterAction>
                <FooterAction>
                  <FooterKey>
                    <CornerDownLeft size={12} aria-hidden="true" />
                  </FooterKey>
                  <span>Open</span>
                </FooterAction>
              </div>
              <FooterAction>
                <FooterKey>{isMac ? <CommandIcon size={12} aria-hidden="true" /> : 'Ctrl'}</FooterKey>
                <FooterKey>K</FooterKey>
                <span>Open search</span>
              </FooterAction>
            </Footer>
          )}
        </SearchContainer>
      </Command.Dialog>
    </>
  );
};

UniversalSearch.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
};
